import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { batchesAPI } from '../services/batches';
import {
  formatScheduleSummary,
  formatTimezoneDetails,
  sortBulkShiftResults,
} from '../utils/scheduleShiftDisplay';

const SHIFT_PRESETS = [
  { label: '+1 hour (DST forward)', value: 60 },
  { label: '-1 hour (DST back)', value: -60 },
  { label: '+45 minutes', value: 45 },
  { label: '-45 minutes', value: -45 },
  { label: '+30 minutes', value: 30 },
  { label: '-30 minutes', value: -30 },
];

const MAX_BATCHES = 100;

function buildPayload(shiftMinutes, selectedBatches) {
  return {
    shift_minutes: shiftMinutes,
    batch_ids: selectedBatches.map((b) => b.id),
  };
}

function formatShiftLabel(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  return minutes > 0 ? `+${minutes} min` : `${minutes} min`;
}

function BulkShiftResultsTable({ rows, showReconcile = false }) {
  const sorted = useMemo(() => sortBulkShiftResults(rows), [rows]);

  return (
    <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width={100}>Status</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Timezone</TableCell>
            <TableCell>Before</TableCell>
            <TableCell>After</TableCell>
            {showReconcile && <TableCell>Reconcile</TableCell>}
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => (
            <TableRow
              key={row.batchId}
              sx={
                row.ok
                  ? undefined
                  : {
                      bgcolor: 'error.light',
                      '& .MuiTableCell-root': { color: 'error.contrastText' },
                      '& .MuiChip-root': {
                        bgcolor: 'error.dark',
                        color: 'error.contrastText',
                      },
                    }
              }
            >
              <TableCell>
                {row.ok ? (
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircle />}
                    label="Success"
                  />
                ) : (
                  <Chip
                    size="small"
                    color="error"
                    icon={<ErrorIcon />}
                    label="Failed"
                  />
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {row.batchName || row.batchId}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 140 }}>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                  {formatTimezoneDetails(row.teacherCountry, row.teacherTimezone)}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 180, fontSize: '0.75rem', verticalAlign: 'top' }}>
                {formatScheduleSummary(row.schedulesBefore)}
              </TableCell>
              <TableCell sx={{ maxWidth: 180, fontSize: '0.75rem', verticalAlign: 'top' }}>
                {row.ok ? formatScheduleSummary(row.schedulesAfter) : '—'}
              </TableCell>
              {showReconcile && (
                <TableCell sx={{ fontSize: '0.75rem', verticalAlign: 'top' }}>
                  {row.ok && row.sessionReconcile ? (
                    <>
                      {(row.sessionReconcile.deletedEmpty || 0) > 0 && (
                        <span>{row.sessionReconcile.deletedEmpty} empty deleted<br /></span>
                      )}
                      {(row.sessionReconcile.retimed || 0) > 0 && (
                        <span>{row.sessionReconcile.retimed} retimed<br /></span>
                      )}
                      {(row.sessionReconcile.skippedCase2 || 0) > 0 && (
                        <span>{row.sessionReconcile.skippedCase2} need review<br /></span>
                      )}
                      {(row.sessionReconcile.createdMissing || 0) > 0 && (
                        <span>{row.sessionReconcile.createdMissing} created</span>
                      )}
                      {!row.sessionReconcile.deletedEmpty &&
                        !row.sessionReconcile.retimed &&
                        !row.sessionReconcile.skippedCase2 &&
                        !row.sessionReconcile.createdMissing &&
                        'No changes'}
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
              )}
              <TableCell sx={{ maxWidth: 200, fontSize: '0.75rem', verticalAlign: 'top' }}>
                {row.ok ? (
                  <Typography variant="caption" sx={{ opacity: row.ok ? 0.85 : 1 }}>
                    Schedules updated
                  </Typography>
                ) : (
                  row.error || 'Unknown error'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function BulkScheduleShiftDialog({
  open,
  onClose,
  selectedBatches = [],
  onRemoveBatch,
  onClearSelection,
  onApplied,
}) {
  const [shiftMinutes, setShiftMinutes] = useState(60);
  const [customMinutes, setCustomMinutes] = useState('');
  const [preview, setPreview] = useState(null);
  const [completionResult, setCompletionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const effectiveShift = customMinutes !== '' ? Number(customMinutes) : shiftMinutes;

  const canSubmit = useMemo(() => {
    if (!selectedBatches.length) return false;
    if (selectedBatches.length > MAX_BATCHES) return false;
    return effectiveShift && effectiveShift !== 0 && !Number.isNaN(effectiveShift);
  }, [effectiveShift, selectedBatches.length]);

  const resetDialog = () => {
    setPreview(null);
    setCompletionResult(null);
    setLoading(false);
    setApplying(false);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const handlePreview = async () => {
    if (!canSubmit) {
      toast.error(`Select 1–${MAX_BATCHES} batches before preview`);
      return;
    }
    setLoading(true);
    setPreview(null);
    setCompletionResult(null);
    try {
      const res = await batchesAPI.previewBulkScheduleShift(buildPayload(effectiveShift, selectedBatches));
      setPreview(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!canSubmit) return;
    const label = formatShiftLabel(effectiveShift);
    if (!window.confirm(`Apply ${label} shift to ${selectedBatches.length} batch(es)?`)) {
      return;
    }
    setApplying(true);
    setCompletionResult(null);
    try {
      const payload = buildPayload(effectiveShift, selectedBatches);
      const res = await batchesAPI.applyBulkScheduleShift(payload);
      const data = res.data?.data || null;
      const summary = data?.summary;
      setCompletionResult(data);
      setPreview(null);

      if (summary?.failed > 0) {
        toast.error(`Updated ${summary.ok} of ${summary.total} batch(es) — ${summary.failed} failed`);
      } else {
        toast.success(res.data?.message || `Updated ${summary?.ok || 0} batch(es)`);
      }
      if (summary?.sessionReconcileTotals?.skippedCase2 > 0) {
        toast.error(`${summary.sessionReconcileTotals.skippedCase2} session(s) need manual review (Case 2)`);
      }
      onApplied?.();
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '');
      toast.error(
        isTimeout
          ? 'Request timed out — the server may still be processing. Refresh the batch list and check schedules before retrying.'
          : err.response?.data?.message || err.message || 'Apply failed'
      );
    } finally {
      setApplying(false);
    }
  };

  const showCompletion = !!completionResult;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {showCompletion ? 'Bulk shift complete' : 'Bulk shift schedules'}
      </DialogTitle>
      <DialogContent dividers>
        {showCompletion ? (
          <Box>
            {completionResult.summary?.failed > 0 ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {completionResult.summary.failed} of {completionResult.summary.total} batch(es) failed.
                Failed batches are listed first below.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                All {completionResult.summary?.total || 0} batch(es) updated successfully (
                {formatShiftLabel(completionResult.summary?.shift_minutes)}).
              </Alert>
            )}

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Shift:</strong> {formatShiftLabel(completionResult.summary?.shift_minutes)}
              </Typography>
              <Typography variant="body2">
                <strong>Success:</strong> {completionResult.summary?.ok || 0}
              </Typography>
              <Typography variant="body2" color={completionResult.summary?.failed ? 'error.main' : 'text.primary'}>
                <strong>Failed:</strong> {completionResult.summary?.failed || 0}
              </Typography>
              {(completionResult.summary?.sessionReconcileTotals?.deletedEmpty > 0 ||
                completionResult.summary?.sessionReconcileTotals?.retimed > 0 ||
                completionResult.summary?.sessionReconcileTotals?.skippedCase2 > 0) && (
                <Typography variant="body2" color="text.secondary">
                  Reconcile: {completionResult.summary.sessionReconcileTotals.deletedEmpty || 0} empty deleted,{' '}
                  {completionResult.summary.sessionReconcileTotals.retimed || 0} retimed,{' '}
                  {completionResult.summary.sessionReconcileTotals.skippedCase2 || 0} need review
                </Typography>
              )}
            </Stack>

            <Typography variant="subtitle2" gutterBottom>
              Results by batch
            </Typography>
            <BulkShiftResultsTable rows={completionResult.batches || []} showReconcile />
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Shifts every schedule slot for each selected batch. Reconcile runs automatically
              (empty orphans removed, data retimed).
            </Alert>

            <Typography variant="subtitle2" gutterBottom>
              Selected batches ({selectedBatches.length}
              {selectedBatches.length > MAX_BATCHES ? ` — max ${MAX_BATCHES}` : ''})
            </Typography>

            {!selectedBatches.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No batches selected. Close this dialog, use the checkboxes in the table, or choose
                &quot;Select all matching filters&quot; above the list.
              </Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 180, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch</TableCell>
                      <TableCell>Teacher TZ</TableCell>
                      <TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBatches.map((batch) => (
                      <TableRow key={batch.id} hover>
                        <TableCell>{batch.name}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimezoneDetails(batch.teacher_country, batch.teacher_timezone)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" color="inherit" onClick={() => onRemoveBatch?.(batch.id)}>
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {selectedBatches.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                {selectedBatches.slice(0, 8).map((b) => (
                  <Chip
                    key={b.id}
                    label={b.name}
                    size="small"
                    onDelete={() => onRemoveBatch?.(b.id)}
                  />
                ))}
                {selectedBatches.length > 8 && (
                  <Chip label={`+${selectedBatches.length - 8} more`} size="small" variant="outlined" />
                )}
                {selectedBatches.length > 0 && (
                  <Button size="small" onClick={onClearSelection}>
                    Clear all
                  </Button>
                )}
              </Stack>
            )}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
              Shift amount
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Shift preset</InputLabel>
              <Select
                label="Shift preset"
                value={customMinutes !== '' ? 'custom' : shiftMinutes}
                onChange={(e) => {
                  if (e.target.value === 'custom') return;
                  setCustomMinutes('');
                  setShiftMinutes(Number(e.target.value));
                }}
              >
                {SHIFT_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
                <MenuItem value="custom">Custom minutes</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Custom shift (minutes)"
              value={customMinutes}
              onChange={(e) => setCustomMinutes(e.target.value)}
              placeholder="e.g. 60 or -60"
              helperText="Positive = later, negative = earlier."
            />

            {preview && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview — {preview.summary?.ok}/{preview.summary?.total} OK
                  {preview.summary?.failed ? `, ${preview.summary.failed} failed` : ''}
                </Typography>
                {preview.summary?.failed > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {preview.summary.failed} batch(es) would fail. They are shown first in red.
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Reconcile: {preview.summary?.sessionReconcileTotals?.deletedEmpty || 0} empty deleted,{' '}
                  {preview.summary?.sessionReconcileTotals?.retimed || 0} retimed,{' '}
                  {preview.summary?.sessionReconcileTotals?.skippedCase2 || 0} need review
                </Typography>
                <BulkShiftResultsTable rows={preview.batches || []} />
              </Box>
            )}

            {applying && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Shifting schedules and reconciling sessions — this can take several minutes for multiple
                batches. Please keep this dialog open.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {showCompletion ? (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={applying}>
              Cancel
            </Button>
            <Button onClick={handlePreview} disabled={!canSubmit || loading || applying}>
              {loading ? <CircularProgress size={20} /> : 'Preview'}
            </Button>
            <Button variant="contained" onClick={handleApply} disabled={!canSubmit || applying || loading}>
              {applying ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} color="inherit" />
                  <span>Applying…</span>
                </Stack>
              ) : (
                'Apply shift'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
